
<app-page-header
  title="Key Frame Generator"
  [subtitle]="i18n.t('kvs.subtitle')"
  [icons]="headerIcons">
</app-page-header>

<div class="key-visual-splitter-container">

  <!-- メインエリア -->
  <div class="main-area">
    <!-- ConfigPanelComponentを使用 -->
    <app-config-panel
      [title]="i18n.t('kvs.configTitle')"
      [isMinimized]="isProcessing || resultImages.length > 0"
      [imageUploadConfig]="imageUploadConfig"
      [promptConfig]="promptConfig"
      [buttonConfig]="buttonConfig"
      [isProcessing]="isProcessing"
      [isGenerateDisabled]="!uploadedFile || !videoPrompt.trim() || isProcessing"
      (fileSelected)="handleFileSelected($event)"
      (promptChanged)="handlePromptChanged($event)"
      (generateClicked)="startGeneration()"
      (validationError)="handleValidationError($event)">
      
      <!-- カスタムセクション: 動画設定と分割数設定 -->
      <div slot="custom-section" class="custom-sections">
        <!-- 動画設定セクション -->
        <div class="video-settings-section">
          <h3 class="section-title">{{ i18n.t('kvs.videoSettingsTitle') }} <span class="optional">{{ i18n.t('kvs.optional') }}</span></h3>
          <div class="video-settings-grid">
            <div class="setting-group">
              <label>{{ i18n.t('kvs.videoSize') }}</label>
              <select [(ngModel)]="selectedVideoSize" [disabled]="isProcessing" class="spectrum-select">
                <option value="540p">540p (960×540)</option>
                <option value="720p">720p (1280×720)</option>
                <option value="1080p">1080p (1920×1080)</option>
              </select>
            </div>
            <div class="setting-group">
              <label>{{ i18n.t('kvs.cameraMotion') }}</label>
              <app-video-settings-camera-only
                [(ngModel)]="videoSettings"
                (settingsChange)="onVideoSettingsChange($event)"
                [disabled]="isProcessing"
                name="videoSettings">
              </app-video-settings-camera-only>
            </div>
          </div>
        </div>

        <!-- 分割数設定セクション -->
        <div class="split-section">
          <h3 class="section-title">{{ i18n.t('kvs.splitTitle') }}</h3>
          <div class="split-control">
            <input 
              type="number" 
              [(ngModel)]="splitCount"
              min="1" 
              max="100" 
              class="split-input"
            >
            <span class="split-label">{{ i18n.t('kvs.splitUnit') }}</span>
          </div>
        </div>
      </div>
    </app-config-panel>

    <!-- メインコンテンツエリア -->
    <div class="content-area">
      <!-- 初期状態 -->
      <div class="empty-state" *ngIf="!isProcessing && resultImages.length === 0">
        <svg class="empty-icon" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke-width="1.5"/>
          <circle cx="8.5" cy="8.5" r="1.5" stroke-width="1.5"/>
          <path d="M21 15l-5-5L5 21" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <h2 class="empty-title">{{ i18n.t('kvs.emptyTitle') }}</h2>
        <p class="empty-text">{{ i18n.t('kvs.emptyText') }}</p>
      </div>

      <!-- 進行状況 -->
      <div class="progress-container" *ngIf="isProcessing">
        <div class="progress-card">
          <h2 class="progress-title">{{ i18n.t('kvs.progressTitle') }}</h2>
          
          <div class="progress-steps">
            <div class="progress-step" 
                 *ngFor="let step of progressSteps; let i = index"
                 [class.active]="step.status === 'active'"
                 [class.completed]="step.status === 'completed'">
              
              <div class="step-line" *ngIf="i < progressSteps.length - 1"></div>
              
              <div class="step-marker">
                <div class="step-icon">
                  <svg *ngIf="step.status === 'completed'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                  </svg>
                  <div *ngIf="step.status === 'active'" class="step-spinner"></div>
                  <span *ngIf="step.status === 'pending'" class="step-number">{{ i + 1 }}</span>
                </div>
              </div>
              
              <div class="step-content">
                <h3 class="step-name">{{ step.name }}</h3>
                <div class="step-progress" *ngIf="step.status === 'active' && step.percentage > 0">
                  <div class="progress-bar">
                    <div class="progress-fill" [style.width.%]="step.percentage"></div>
                  </div>
                  <span class="progress-text">{{ step.percentage }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 結果表示 -->
      <div class="results-container" *ngIf="!isProcessing && resultImages.length > 0">
        <div class="results-header">
          <h2 class="results-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            {{ i18n.t('kvs.resultTitle') }}
          </h2>
          <p class="results-subtitle">{{ i18n.t('kvs.resultSubtitle', { count: resultImages.length }) }}</p>
        </div>

        <!-- 2カラムレイアウト -->
        <div class="results-layout">
          <!-- 左側：動画セクション -->
          <div class="video-section" *ngIf="generatedVideoUrl">
            <div class="section-header">
              <h3 class="section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                {{ i18n.t('kvs.videoSectionTitle') }}
              </h3>
            </div>
            <div class="video-card">
              <div class="video-wrapper">
                <video 
                  [src]="generatedVideoUrl" 
                  controls 
                  preload="metadata"
                  class="generated-video">
                  {{ i18n.t('kvs.videoNotSupported') }}
                </video>
                <div class="video-info">
                  <span class="video-label">Generated Video</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 右側：フレーム分割セクション -->
          <div class="frames-section">
            <div class="section-header">
              <h3 class="section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                {{ i18n.t('kvs.framesTitle', { count: resultImages.length }) }}
              </h3>
              
              <!-- 分割数変更コントロール -->
              <div class="split-controls">
                <div class="split-input-group">
                  <input 
                    type="number" 
                    [(ngModel)]="newSplitCount"
                    min="1" 
                    max="100" 
                    class="split-input-small"
                    [disabled]="isSplitting"
                  >
                  <span class="split-label">{{ i18n.t('kvs.splitUnit') }}</span>
                </div>
                <button 
                  class="resplit-btn" 
                  [disabled]="!generatedVideoUrl || isSplitting || newSplitCount === splitCount"
                  (click)="resplitVideo()"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" *ngIf="!isSplitting">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                  <div class="spinner" *ngIf="isSplitting"></div>
                  {{ isSplitting ? i18n.t('kvs.splitting') : i18n.t('kvs.resplit') }}
                </button>
              </div>
            </div>
            <div class="frames-grid">
              <div class="frame-card" *ngFor="let image of resultImages; let i = index">
                <div class="frame-image-wrapper">
                  <img [src]="image" [alt]="i18n.t('kvs.frameAlt') + ' ' + (i + 1)" class="frame-image">
                  <div class="frame-overlay">
                    <div class="frame-number">{{ i + 1 }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- エラー表示 -->
  <div class="error-toast" *ngIf="errorMessage" (click)="errorMessage = ''">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
    </svg>
    <span>{{ errorMessage }}</span>
  </div>
</div>